import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Divider,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import apiClient from './Api.js';

function Branches() {
  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [loading, setLoading] = useState(true);
  const [companyBranches, setCompanyBranches] = useState([]);
  
  const [isEditingBranches, setIsEditingBranches] = useState(false);
  const [originalBranches, setOriginalBranches] = useState([]);
  const [openAddBranchDialog, setOpenAddBranchDialog] = useState(false);
  const [newBranch, setNewBranch] = useState({
    name: '',
    code: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    isActive: true
  });

  // Show notification
  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };


  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getBranches();
      const data = Array.isArray(response) ? response : response.results || [];
      setCompanyBranches(data);
    } catch (error) {
      showNotification('Failed to load branches: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle notification close
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };

  // Company Branches Functions
  const handleEditBranches = () => {
    setOriginalBranches(JSON.parse(JSON.stringify(companyBranches)));
    setIsEditingBranches(true);
  };

  const handleCancelBranches = () => {
    setCompanyBranches(JSON.parse(JSON.stringify(originalBranches)));
    setIsEditingBranches(false);
  };

  const handleSaveBranches = async () => {
  try {
    await apiClient.bulkUpdateBranches(companyBranches);
    setIsEditingBranches(false);
    showNotification('Company branches saved successfully');
  } catch (error) {
    showNotification('Failed to save branches: ' + error.message, 'error');
  }
};

  const handleBranchChange = (id, field) => (event) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    const updatedBranches = companyBranches.map(branch => {
      if (branch.id === id) {
        return { ...branch, [field]: value };
      }
      return branch;
    });
    setCompanyBranches(updatedBranches);
  };

  const handleDeleteBranch = async (id) => {
    try {
      await apiClient.deleteBranch(id);
      setCompanyBranches(companyBranches.filter(branch => branch.id !== id));
      showNotification('Branch removed');
    } catch (error) {
      showNotification('Failed to delete branch: ' + error.message, 'error');
    }
  };
  

  const handleAddBranch = async () => {
    // Validate form
    if (!newBranch.name || !newBranch.code) {
      showNotification('Please fill in branch name and code', 'error');
      return;
    }
  
    // Check for duplicate code
    if (companyBranches.some(b => b.code.toLowerCase() === newBranch.code.toLowerCase())) {
      showNotification('Branch code already exists', 'error');
      return;
    }
  
    try {
      const branchToAdd = {
        ...newBranch,
        code: newBranch.code.toUpperCase()
      };
  
      const createdBranch = await apiClient.createBranch(branchToAdd);
      setCompanyBranches([...companyBranches, createdBranch]);
      setOpenAddBranchDialog(false);
      
      // Reset form
      setNewBranch({
        name: '',
        code: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
        email: '',
        isActive: true
      });
      
      showNotification('New branch added successfully');
    } catch (error) {
      showNotification('Failed to add branch: ' + error.message, 'error');
    }
  };

  const handleNewBranchChange = (field) => (event) => {
    const value = field === 'isActive' ? event.target.checked : event.target.value;
    setNewBranch({
      ...newBranch,
      [field]: value
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Box>
      {/* Company Branches Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon />
            Company Branches
          </Typography>
          {!isEditingBranches ? (
            <Button 
              variant="outlined" 
              startIcon={<EditIcon />} 
              onClick={handleEditBranches}
            >
              Edit
            </Button>
          ) : null}
        </Box>

        <Paper elevation={isEditingBranches ? 2 : 0} sx={{ p: isEditingBranches ? 3 : 0 }}>
          <Grid container spacing={2}>
            {companyBranches.map((branch) => (
              <Grid item xs={12} md={6} key={branch.id}>
                <Box sx={{ 
                    p: 2, 
                    border: 1, 
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.default',
                    opacity: branch.isActive ? 1 : 0.7
                  }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isEditingBranches ? (
                        <TextField
                          value={branch.name}
                          onChange={handleBranchChange(branch.id, 'name')}
                          variant="outlined"
                          size="small"
                          sx={{ minWidth: '200px' }}
                        />
                      ) : (
                        <Typography variant="subtitle1" fontWeight="medium">{branch.name}</Typography>
                      )}
                      <Chip 
                        label={branch.code} 
                        size="small" 
                        color="primary"
                      />
                      <Chip 
                        label={branch.isActive ? 'Active' : 'Inactive'} 
                        size="small" 
                        color={branch.isActive ? 'success' : 'warning'}
                      />
                    </Box>
                    {isEditingBranches && (
                      <IconButton 
                        color="error" 
                        size="small" 
                        onClick={() => handleDeleteBranch(branch.id)}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  
                  {isEditingBranches ? (
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Address"
                          value={branch.address}
                          onChange={handleBranchChange(branch.id, 'address')}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="City"
                          value={branch.city}
                          onChange={handleBranchChange(branch.id, 'city')}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="State"
                          value={branch.state}
                          onChange={handleBranchChange(branch.id, 'state')}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label="Zip Code"
                          value={branch.zipCode}
                          onChange={handleBranchChange(branch.id, 'zipCode')}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Phone"
                          value={branch.phone}
                          onChange={handleBranchChange(branch.id, 'phone')}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          value={branch.email}
                          onChange={handleBranchChange(branch.id, 'email')}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={branch.isActive}
                              onChange={handleBranchChange(branch.id, 'isActive')}
                            />
                          }
                          label="Active"
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {branch.address}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        {branch.city}, {branch.state} {branch.zipCode}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Phone: {branch.phone}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Email: {branch.email}
                      </Typography>
                    </>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>

          {isEditingBranches && (
            <>
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                  variant="contained"
                  color="success"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenAddBranchDialog(true)}
                >
                  Add New Branch
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenAddBranchDialog(true)}
                  sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#388e3c' } }}
                >
                  Add New Branch
                </Button>
              </Box>

              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCancelBranches}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveBranches}
                  color="primary"
                >
                  Save Branches
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Box>

      {/* Add Branch Dialog */}
      <Dialog 
        open={openAddBranchDialog} 
        onClose={() => setOpenAddBranchDialog(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Add New Branch</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Branch Name"
                value={newBranch.name}
                onChange={handleNewBranchChange('name')}
                variant="outlined"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Branch Code"
                value={newBranch.code}
                onChange={handleNewBranchChange('code')}
                variant="outlined"
                required
                placeholder="e.g. MAIN, WEST, EAST"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={newBranch.address}
                onChange={handleNewBranchChange('address')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={newBranch.city}
                onChange={handleNewBranchChange('city')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={newBranch.state}
                onChange={handleNewBranchChange('state')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Zip Code"
                value={newBranch.zipCode}
                onChange={handleNewBranchChange('zipCode')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={newBranch.phone}
                onChange={handleNewBranchChange('phone')}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                value={newBranch.email}
                onChange={handleNewBranchChange('email')}
                variant="outlined"
                type="email"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={newBranch.isActive}
                    onChange={handleNewBranchChange('isActive')}
                  />
                }
                label="Active Branch"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddBranchDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddBranch} 
            variant="contained" 
            color="primary"
          >
            Add Branch
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Branches;